import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { isEmpty } from 'lodash-es'
import styled, { ThemeProvider as StyledComponentsThemeProvider, useTheme } from 'styled-components/native'

import { NeurodiversityContactCareNavigatorEntryPoint } from '@lyrahealth-inc/shared-app-logic/src/features/centerOfExcellence/types'
import {
  isToolkitTypeHealthChallenges,
  isToolkitTypeNeurodiversity,
  isToolkitTypeWomensHealth,
} from '@lyrahealth-inc/shared-app-logic/src/features/centerOfExcellence/utils'
import {
  COMMON_ENTRY_POINTS,
  COMMON_MIXPANEL_PAGE,
  TRIAGE_ENTRY_POINTS,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  HEALTH_CHALLENGES_SECURE_BASE_ROUTE,
  MANAGER_HUB_BASE_ROUTE,
  NEURODIVERSITY_SECURE_BASE_ROUTE,
  PAGE_ROUTES,
  PEDIATRICS_SECURE_BASE_ROUTE,
  WOMENS_HEALTH_SECURE_BASE_ROUTE,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { TREATMENT_OPTIONS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptions'
import { FavoritedProvidersEntrypointNavProps } from '@lyrahealth-inc/shared-app-logic/src/features/header/types'
import {
  ContactCareFlowType,
  DirectLinkSource,
} from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { useBackToTriage } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/hooks/useBackToTriage'
import { useInProgressSearch } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/hooks/useInProgressSearch'
import {
  TOOLKIT_TYPE,
  WELLNESS_TOOLKIT_PROMOTION,
  WELLNESS_TOOLKIT_RETURN_DESTINATION,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/types'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { ProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { TriageSearch } from '@lyrahealth-inc/shared-app-logic/src/models/searchForCare/SearchForCare'
import { NavBar } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/navBar/NavBar'
import { OnboardingExitModal } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/onboardingExitModal/OnboardingExitModal'
import { ProgressBar } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/progressBar/ProgressBar'
import { toJS } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/hocs/toJS'
import { ProductTypeContext } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/productType/productTypeContext'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { ThemeScope } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeScope'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { getAppointmentReschedule, getSessions } from '../../../data/lyraTherapy/lyraTherapySelectors'
import { trackEventWithObj } from '../../../data/mixpanel/mixpanel'
import { getRedirectUrlOnTriageExit, getTriageEntryPoint } from '../../../data/register/registerSelectors'
import { getDirectDedicatedCareNavigatorLink } from '../../../data/user/userActions'
import { getUserLegalFirstName } from '../../../data/user/userSelectors'
import { handleZeroViewSelect } from '../../../features/appointmentDashboard/data/appointmentDashboardActions'
import { getNeurodiversitySearcherInfo } from '../../../features/centerOfExcellence/centerOfExcellenceSelector'
import { markDashboardAsStale } from '../../../features/lyraTherapy/data/LyraTherapyActions'
import { setIsLatestMatchesCleared, setLatestMatchesSearch } from '../../../features/onboard/data/onboardDataActions'
import {
  getIsLatestMatches,
  getIsLatestMatchesCleared,
  getLatestMatchesSearch,
  getOnboardDirectLinkSource,
  getOnboardOneLyraToolkitReturnDestination,
  getOnboardProviderProfileData,
  getOnboardToolkitType,
} from '../../../features/onboard/data/onboardSelectors'
import { clearTriage, cloneTriageSearch, setSelectedSearchId } from '../../../features/triage/data/triageActions'
import {
  getProgressBarPercentage,
  getSearches,
  getSelectedSearch,
  getSelectedSearchPrimaryNeed,
  getTreatmentRecommendations,
} from '../../../features/triage/data/triageSelectors'
import { NEGATIVE_PADDING_PATHS } from '../../constants/routingConstants'
import { useCareNavigatorModal } from '../../hooks/useCareNavigatorModal'
import { useNavigateToLatestProviderMatches } from '../../hooks/useNavigateToLatestProviderMatches'
import { useNavigateToWellnessToolkit } from '../../hooks/useNavigateToWellnessToolkit'
import { matchPaths } from '../../utils/stringUtils'

type OnboardingNavBarProps = ConnectedProps<typeof connector> & {
  transparencyEnabled?: boolean
  hasConnectedCalendar: boolean
  isStatic?: boolean
  skipExitModalWhenNoProviderConnectedCalendar?: boolean
  shouldShowCareNavigatorHeaderEntrypoint?: boolean
  percentProgress?: number
  showProgressBar?: boolean
  progressAnimationDuration?: number
  onBack?: (...args: any) => void
  oatmealBackground?: boolean
  padBottom?: boolean
  setLatestMatchesSearch: () => void
  setIsLatestMatchesCleared: (value: boolean) => void
  latestMatchSearch?: TriageSearch
  providerProfileData: ProviderInfo
  searches: TriageSearch[]
  providerSessions: { appointments: Appointment[] }
  isLatestMatches: boolean
  showBackButton?: boolean
  negativePadBottom?: boolean
  testId?: string
  containerWhichIsNotWindowHasScrolled?: boolean
  toolkitType?: TOOLKIT_TYPE
  oneLyraToolkitReturnDestination?: WELLNESS_TOOLKIT_RETURN_DESTINATION
  currentDirectLinkSource?: DirectLinkSource
  favoritedProvidersEntrypointNavProps?: FavoritedProvidersEntrypointNavProps
  headerBackgroundColor?: string
}

const NavBarContainer = styled.View<{
  backgroundColor?: string
  isTransparent?: boolean
  showBorder?: boolean
  isStatic?: boolean
  theme: ThemeType
}>(({ backgroundColor, theme, isTransparent, showBorder, isStatic }) => ({
  position: isStatic ? 'static' : 'sticky',
  top: '0px',
  left: '0px',
  right: '0px',
  zIndex: 100,
  backgroundColor: isTransparent
    ? 'transparent'
    : (backgroundColor ?? theme.colors.components.onboardingNavBarDefaultBackground.background),
  borderBottomWidth: showBorder ? `1px` : undefined,
  borderBottomColor: showBorder ? theme.colors.borderDefault : undefined,
}))

const ProgressBarContainer = styled(ProgressBar)<{ theme: ThemeType }>(({ theme }) => ({
  marginHorizontal: theme.breakpoints.isMinWidthTablet ? theme.spacing['24px'] : theme.spacing['16px'],
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['12px'] : theme.spacing['16px'],
}))

const OnboardingNavBar: React.FC<OnboardingNavBarProps> = ({
  transparencyEnabled = true,
  showProgressBar = true,
  isStatic = false,
  treatmentRecs,
  hasConnectedCalendar,
  skipExitModalWhenNoProviderConnectedCalendar = false,
  shouldShowCareNavigatorHeaderEntrypoint = true,
  percentProgressFromStore,
  percentProgress: percentProgressFromProps,
  onBack,
  markDashboardAsStale,
  oatmealBackground,
  padBottom,
  clearTriage,
  setLatestMatchesSearch,
  setIsLatestMatchesCleared,
  latestMatchSearch,
  providerProfileData,
  searches,
  providerSessions,
  setSelectedSearchId,
  handleZeroViewSelect,
  isLatestMatches,
  showBackButton,
  negativePadBottom,
  testId,
  selectedSearch,
  cloneTriageSearch,
  triageEntryPoint,
  redirectUrlOnTriageExit,
  progressAnimationDuration = 300,
  containerWhichIsNotWindowHasScrolled,
  toolkitType,
  oneLyraToolkitReturnDestination,
  currentDirectLinkSource,
  favoritedProvidersEntrypointNavProps,
  headerBackgroundColor,
}) => {
  const parentTheme = useTheme() as ThemeType
  const { colors, isAuroraTheme } = parentTheme
  const { isLyraSelect } = useContext(ProductTypeContext)
  const navigate = useNavigate()
  const [hasScrolled, setHasScrolled] = useState(false)
  const containerScrollTrackingRef = useRef(containerWhichIsNotWindowHasScrolled)
  useEffect(() => {
    if (
      containerWhichIsNotWindowHasScrolled !== undefined &&
      containerWhichIsNotWindowHasScrolled != containerScrollTrackingRef.current
    ) {
      setHasScrolled(containerWhichIsNotWindowHasScrolled)
      containerScrollTrackingRef.current = containerWhichIsNotWindowHasScrolled
    }
  }, [containerWhichIsNotWindowHasScrolled, hasScrolled])

  const [containerHeight, setContainerHeight] = useState(96)
  const percentProgress = percentProgressFromProps ?? percentProgressFromStore
  const isManagerHubTriageFlow = currentDirectLinkSource === DirectLinkSource.MANAGER_HUB

  const { careNavigatorModal, careNavigatorEntryPoint } = useCareNavigatorModal({
    clickEntryPoint: COMMON_MIXPANEL_PAGE.GLOBAL_NAV_CARE_NAVIGATOR,
  })

  const navigateBackToTriage = useBackToTriage({
    goBack: () => {
      navigate(-1)
    },
    search: selectedSearch as TriageSearch,
    cloneTriageSearch,
  })

  const setHeaderTransparency = useCallback(() => {
    if (window.scrollY === 0) {
      setHasScrolled(false)
    } else {
      setHasScrolled(true)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', setHeaderTransparency)
    return () => {
      window.removeEventListener('scroll', setHeaderTransparency)
    }
  }, [setHeaderTransparency])

  const [isModalVisible, setIsModalVisible] = useState(false)
  const { pathname, state, search } = useLocation()
  const shouldUseLightToolkitHeader = matchPaths(pathname, [
    `/secure/wellness/toolkit/${WELLNESS_TOOLKIT_PROMOTION.PURPOSE}`,
    `/secure/wellness/toolkit/${WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS}`,
  ])
  const isFavoritedProvidersPage = pathname.includes('/secure/index/favorited-providers')
  const shouldUseParentTheme = shouldUseLightToolkitHeader || isFavoritedProvidersPage
  const searchParams = new URLSearchParams(search)
  const entryPoint = searchParams.get('entryPoint')
  const shouldShowBackButton = showBackButton !== false && searchParams.get('showBackButton') !== 'false'
  const fromPathName = state?.from
  const inProgressSearch = useInProgressSearch(searches, providerSessions.appointments)
  const previouslySelectedAppointment = useSelector(getAppointmentReschedule)
  const displayUpdatedConnectedCalendar = !previouslySelectedAppointment

  const navigateToLatestProviderMatches = useNavigateToLatestProviderMatches({
    setLatestMatchesSearch,
    setSelectedSearchId,
    inProgressSearch,
    handleZeroViewSelect,
  })
  const navigateToWellnessToolkit = useNavigateToWellnessToolkit()
  const isLatestMatchesCleared = useSelector(getIsLatestMatchesCleared)

  const isNeurodiversityFlow =
    isToolkitTypeNeurodiversity(toolkitType) || state?.flowType === ContactCareFlowType.NEURODIVERSITY
  const shouldReturnToNeurodiversityToolkit = isToolkitTypeNeurodiversity(toolkitType)
  const neurodiversitySearcherInfo = useSelector(getNeurodiversitySearcherInfo)
  const isNeurodiversityConsultationPage =
    pathname.startsWith(PAGE_ROUTES.CONTACT_CARE_ADVOCATE_FORM) &&
    state?.flowType === ContactCareFlowType.NEURODIVERSITY &&
    neurodiversitySearcherInfo?.get('contactCareNavEntryPoint') ===
      NeurodiversityContactCareNavigatorEntryPoint.NEURODIVERSITY_NAVIGATION
  const isHealthChallengesFlow = isToolkitTypeHealthChallenges(toolkitType)
  const isWomensHealthFlow = isToolkitTypeWomensHealth(toolkitType)
  const shouldReturnToPediatricsToolkit = triageEntryPoint === TRIAGE_ENTRY_POINTS.PEDIATRICS_TOOLKIT

  // Hide the care navigator entrypoint for care navigator pages
  if (shouldShowCareNavigatorHeaderEntrypoint) {
    shouldShowCareNavigatorHeaderEntrypoint =
      !matchPaths(pathname, [
        PAGE_ROUTES.CONTACT_CARE_ADVOCATE_FORM,
        PAGE_ROUTES.CONTACT_CARE_ADVOCATE_CONFIRMATION,
        '/secure/onboard/care-navigator-options',
        '/secure/onboard/contact-care-team',
        '/secure/onboard/contact-care-team/confirmation',
      ]) || Boolean(isNeurodiversityFlow)
  }

  // No exit modal for these pages.
  skipExitModalWhenNoProviderConnectedCalendar = matchPaths(pathname, [
    '/secure/onboard/provider/:id',
    '/secure/onboard/provider/:id/request',
    'secure/onboard/contact-care-team/confirmation',
  ])

  // Oatmeal color pages
  oatmealBackground =
    oatmealBackground ||
    matchPaths(pathname, [
      '/secure/onboard/match-location',
      '/secure/wellness/trauma-checkin',
      '/secure/onboard/member-preferences',
      '/secure/onboard/member-preferences/selection',
      '/secure/onboard/meds-options-explained',
      '/secure/onboard/more-options',
      '/secure/onboard/zero-search-results-redirect',
      '/secure/onboard/care-navigator-options',
      PAGE_ROUTES.ONBOARD.CHILD_CARE_NAVIGATOR_OPTIONS,
      PAGE_ROUTES.CONTACT_CARE_ADVOCATE_FORM,
      PAGE_ROUTES.CONTACT_CARE_ADVOCATE_CONFIRMATION,
      '/secure/onboard/contact-care-team/confirmation',
      '/secure/onboard/about-child',
      '/secure/onboard/about-child/info',
      '/secure/onboard/custom-child-care',
      '/secure/onboard/provider/:id/appointment',
      '/secure/onboard/provider/:id/request',
      ...(displayUpdatedConnectedCalendar ? ['/secure/onboard/provider/:id/schedule'] : []),
    ])

  // Need to add extra negative padding so that nav sits on top of image header
  negativePadBottom = matchPaths(
    pathname,
    NEGATIVE_PADDING_PATHS.concat('/secure/onboard/treatment-options', '/secure/wellness/toolkit/:id'),
  )

  const disableTransparency = isLyraSelect
    ? [PAGE_ROUTES.CARE_NAVIGATOR_OPTIONS.ONBOARD, PAGE_ROUTES.ONBOARD.CHILD_CARE_NAVIGATOR_OPTIONS]
    : []

  transparencyEnabled = !matchPaths(pathname, [
    '/secure/onboard/member-preferences',
    '/secure/onboard/member-preferences/selection',
    '/secure/wellness/trauma-checkin',
    '/secure/onboard/about-child',
    '/secure/onboard/about-child/info',
    '/secure/onboard/custom-child-care',
    '/secure/onboard/provider/:id/appointment',
    '/secure/onboard/provider/:id/request',
    '/secure/onboard/provider/:id',
    PAGE_ROUTES.CONTACT_CARE_ADVOCATE_FORM,
    PAGE_ROUTES.CONTACT_CARE_ADVOCATE_CONFIRMATION,
    '/secure/onboard/contact-care-team/confirmation',
    ...disableTransparency,
  ])

  showProgressBar =
    !matchPaths(pathname, [
      PAGE_ROUTES.CONTACT_CARE_ADVOCATE_FORM,
      PAGE_ROUTES.CENTER_OF_EXCELLENCE.NEURODIVERSITY.ONBOARD_CONTACT_CARE_NAVIGATOR,
      PAGE_ROUTES.CENTER_OF_EXCELLENCE.PEDIATRICS.ONBOARD_CONTACT_CARE_NAVIGATOR,
      '/secure/onboard/contact-care-team/confirmation',
      '/secure/onboard/care-navigator-options',
      '/secure/index/provider/:id/therapy-care-options',
      '/secure/index/provider/:id/coaching-options',
    ]) &&
    treatmentRecs?.primaryTreatmentOptions?.[0] !== TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD_ICAS_PHONE &&
    treatmentRecs?.primaryTreatmentOptions?.[0] !== TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE

  const isNeuroCareAdvocatePage =
    isNeurodiversityFlow &&
    matchPaths(pathname, [PAGE_ROUTES.CONTACT_CARE_ADVOCATE_FORM, PAGE_ROUTES.CONTACT_CARE_ADVOCATE_CONFIRMATION])
  const showExitButton = isNeuroCareAdvocatePage
    ? true
    : isNeurodiversityFlow
      ? triageEntryPoint === TRIAGE_ENTRY_POINTS.NEURODIVERSITY
      : !matchPaths(pathname, ['/secure/wellness/toolkit/:id'])
  const hideBorder =
    matchPaths(pathname, [...(displayUpdatedConnectedCalendar ? ['/secure/onboard/provider/:id/schedule'] : [])]) ||
    isLyraSelect

  const resetTriageToHomebase = () => {
    const entry = triageEntryPoint
    clearTriage()
    markDashboardAsStale()
    setLatestMatchesSearch(null)
    const redirectTriagePoints = [TRIAGE_ENTRY_POINTS.ESSENTIALS, TRIAGE_ENTRY_POINTS.CARE_TAB]
    if (redirectTriagePoints.includes(entry)) {
      navigate(redirectUrlOnTriageExit)
    } else {
      navigate('/secure/index/search-care')
    }
  }

  const resetCenterOfExcellenceFlow = (landingPage: string) => {
    clearTriage()
    markDashboardAsStale()
    setLatestMatchesSearch(null)
    const returnDestinationQuery = oneLyraToolkitReturnDestination
      ? `?returnDestination=${encodeURIComponent(oneLyraToolkitReturnDestination)}`
      : ''
    navigate(`${landingPage}${returnDestinationQuery}`)
  }

  const resetTriageToManagerHub = () => {
    clearTriage()
    markDashboardAsStale()
    setLatestMatchesSearch(null)
    navigate(MANAGER_HUB_BASE_ROUTE)
  }

  const getBackgroundColor = () => {
    const defaultBackgroundColor = colors.components.onboardingNavBarDefaultBackground.background
    const backgroundColor = headerBackgroundColor ? headerBackgroundColor : defaultBackgroundColor
    if (oatmealBackground) {
      if (isStatic) {
        return colors.backgroundSecondary
      }
      return !hasScrolled ? colors.backgroundSecondary : backgroundColor
    }
    return backgroundColor
  }

  const shouldNavigateBackThroughDirectPathHistory =
    entryPoint === COMMON_ENTRY_POINTS.HOMEBASE_CARE_CAROUSEL && fromPathName !== PAGE_ROUTES.HOMEBASE

  const renderWithParentTheme = (content: React.ReactElement | undefined): React.ReactElement | undefined => {
    if (!content) return undefined
    return shouldUseParentTheme ? (
      <StyledComponentsThemeProvider theme={parentTheme}>{content}</StyledComponentsThemeProvider>
    ) : (
      content
    )
  }

  return (
    <ThemeScope
      forceDarkMode={!isNeurodiversityConsultationPage && !shouldUseParentTheme}
      forceLightMode={shouldUseLightToolkitHeader}
    >
      <NavBarContainer
        testID={tID('OnboardingNavBar')}
        backgroundColor={shouldUseParentTheme ? undefined : getBackgroundColor()}
        isTransparent={(transparencyEnabled && !hasScrolled) || isAuroraTheme}
        showBorder={hideBorder || isStatic || isAuroraTheme ? false : hasScrolled}
        onLayout={(e) => {
          setContainerHeight(e.nativeEvent.layout.height)
        }}
        isStatic={isStatic}
      >
        <NavBar
          testId={testId}
          showBackButton={shouldShowBackButton}
          favoritedProvidersEntrypointNavProps={favoritedProvidersEntrypointNavProps}
          onBack={() => {
            if (shouldNavigateBackThroughDirectPathHistory) {
              navigate(-1)
            } else if (entryPoint) {
              resetTriageToHomebase()
            } else if (latestMatchSearch && isEmpty(providerProfileData)) {
              resetTriageToHomebase()
            } else if (
              fromPathName &&
              matchPaths(fromPathName, [
                '/secure/onboard/t1-multi-providers/latest-provider-matches',
                '/secure/onboard/coaches/latest-provider-matches',
              ])
            ) {
              if (isLatestMatchesCleared) {
                // If we are coming from saved provider results matches, the triage state is cleared
                // so redirect to homebase instead to prevent errors
                setIsLatestMatchesCleared(false)
                resetTriageToHomebase()
              } else {
                navigateToLatestProviderMatches()
              }
            } else if (
              pathname &&
              matchPaths(pathname, [
                PAGE_ROUTES.ONBOARD.CHILD_CARE_NAVIGATOR_OPTIONS,
                PAGE_ROUTES.CARE_NAVIGATOR_OPTIONS.ONBOARD,
              ])
            ) {
              navigateBackToTriage()
            } else if (onBack) {
              onBack()
            } else {
              navigate(-1)
            }
          }}
          onExit={
            showExitButton
              ? () => {
                  if (skipExitModalWhenNoProviderConnectedCalendar && !hasConnectedCalendar) {
                    if (isManagerHubTriageFlow) {
                      resetTriageToManagerHub()
                    } else if (shouldReturnToNeurodiversityToolkit) {
                      resetCenterOfExcellenceFlow(NEURODIVERSITY_SECURE_BASE_ROUTE)
                    } else if (shouldReturnToPediatricsToolkit) {
                      resetCenterOfExcellenceFlow(PEDIATRICS_SECURE_BASE_ROUTE)
                    } else {
                      navigate('/secure/index/search-care')
                    }
                  } else if (pathname.endsWith('/secure/wellness/trauma-checkin')) {
                    // Skip exit modal for trauma checkin and go back to trauma toolkit
                    navigate(-1)
                  } else {
                    setIsModalVisible(true)
                  }
                }
              : undefined
          }
          isTransparent={true}
          shouldShowCareNavigatorHeaderEntrypoint={shouldShowCareNavigatorHeaderEntrypoint}
          careNavigatorModal={renderWithParentTheme(careNavigatorModal)}
          careNavigatorEntryPoint={careNavigatorEntryPoint}
          {...((isNeurodiversityFlow ||
            matchPaths(pathname, ['/secure/wellness/toolkit/:id', '/secure/wellness/trauma-checkin'])) && {
            onLogoPress: () => navigate(PAGE_ROUTES.HOMEBASE),
          })}
          isBlurBackground={isAuroraTheme && hasScrolled}
        />
        {showProgressBar && Boolean(percentProgress) && (
          <ProgressBarContainer
            progress={percentProgress}
            backgroundColor={[colors.progressIndicatorBackgroundTop, colors.progressIndicatorBackgroundBottom]}
            color={colors.progressIndicator}
            width={-1}
            height={4}
            borderRadius={4}
            duration={progressAnimationDuration}
            showStopIndicator
          />
        )}
        {renderWithParentTheme(
          <OnboardingExitModal
            isVisible={isModalVisible}
            onExit={() => {
              // If user entered triage from Wellness Toolkit, always return to that toolkit on exit
              const shouldReturnToToolkit =
                (triageEntryPoint === TRIAGE_ENTRY_POINTS.WELLNESS_TOOLKIT ||
                  selectedSearch?.body.programPreference !== undefined) &&
                toolkitType !== undefined

              // Basic Manager Hub intentionally uses the Work toolkit type to preselect Work in PAT,
              // so its explicit origin must take precedence over the generic toolkit return behavior.
              if (isManagerHubTriageFlow) {
                resetTriageToManagerHub()
              } else if (shouldReturnToToolkit) {
                resetTriageToHomebase()
                setIsModalVisible(false)
                navigateToWellnessToolkit({
                  toolkitPromotion: toolkitType,
                  directLinkSource: currentDirectLinkSource ?? DirectLinkSource.HOMEBASE_WELLNESS_TOOLKIT_CTA,
                  navigationParams: oneLyraToolkitReturnDestination
                    ? { returnDestination: oneLyraToolkitReturnDestination }
                    : undefined,
                })
              } else if (shouldReturnToNeurodiversityToolkit) {
                resetCenterOfExcellenceFlow(NEURODIVERSITY_SECURE_BASE_ROUTE)
              } else if (isHealthChallengesFlow) {
                resetCenterOfExcellenceFlow(HEALTH_CHALLENGES_SECURE_BASE_ROUTE)
              } else if (isWomensHealthFlow) {
                resetCenterOfExcellenceFlow(WOMENS_HEALTH_SECURE_BASE_ROUTE)
              } else if (shouldReturnToPediatricsToolkit) {
                resetCenterOfExcellenceFlow(PEDIATRICS_SECURE_BASE_ROUTE)
              } else {
                resetTriageToHomebase()
              }
            }}
            onCancel={() => {
              setIsModalVisible(false)
            }}
            isLatestMatches={isLatestMatches}
          />,
        )}
      </NavBarContainer>
      {padBottom && <View style={{ marginBottom: containerHeight }} />}
      {negativePadBottom && <View style={{ marginBottom: -containerHeight }} />}
    </ThemeScope>
  )
}

const mapStateToProps = ($$state: any) => ({
  // If re-enabling this nav bar, we don't depend on connected calendar anymore so should reconsider logic here
  hasConnectedCalendar: false,
  name: getUserLegalFirstName($$state),
  latestMatchSearch: getLatestMatchesSearch($$state),
  providerProfileData: getOnboardProviderProfileData($$state),
  selectedSearch: getSelectedSearch($$state),
  providerSessions: getSessions($$state),
  searches: getSearches($$state),
  isLatestMatches: getIsLatestMatches($$state),
  treatmentRecs: getTreatmentRecommendations($$state),
  primaryNeed: getSelectedSearchPrimaryNeed($$state),
  triageEntryPoint: getTriageEntryPoint($$state),
  redirectUrlOnTriageExit: getRedirectUrlOnTriageExit($$state),
  percentProgressFromStore: getProgressBarPercentage($$state),
  toolkitType: getOnboardToolkitType($$state),
  oneLyraToolkitReturnDestination: getOnboardOneLyraToolkitReturnDestination($$state),
  currentDirectLinkSource: getOnboardDirectLinkSource($$state),
  isLatestMatchesCleared: getIsLatestMatchesCleared($$state),
})

const mapDispatchToProps = {
  trackEventWithObj,
  getDirectDedicatedCareNavigatorLink,
  markDashboardAsStale,
  clearTriage,
  setLatestMatchesSearch,
  setIsLatestMatchesCleared,
  setSelectedSearchId,
  handleZeroViewSelect,
  cloneTriageSearch,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(toJS(OnboardingNavBar))
